import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, User, MapPin, Phone, Stethoscope, Heart,
  Brain, CheckCircle2, Circle, Clock, Building2, Calendar,
  ShieldAlert, Activity, Sparkles, ArrowRight, RefreshCw,
  ClipboardEdit, Eye, MoreVertical, CalendarCheck,
} from 'lucide-react';
import { patientDetails } from '@/data/mockCaseManagerData';
import PatientRecordModal from '@/components/PatientRecordModal';
import ClinicalGantt from '@/components/ClinicalGantt';
import { ganttData } from '@/data/mockCaseManagerData';
import AssegnazioneProviderDrawer from '@/components/AssegnazioneProviderDrawer';
import PaiReevaluationScreen from '@/components/PaiReevaluationScreen';
import PaiManualCompilation from '@/components/wizard/PaiManualCompilation';

/* helper: calcola giorni tra due date dd/MM/yyyy */
function daysBetween(d1, d2) {
  if (!d1 || !d2) return null;
  const parse = (s) => { const p = s.split('/'); return new Date(p[2], p[1] - 1, p[0]); };
  return Math.round((parse(d2) - parse(d1)) / 86400000);
}

/* helper: controlla se uno step specifico e nello stato dato */
function isStepInState(steps, label, stato) {
  const step = steps.find(s => s.label === label);
  return step?.stato === stato;
}
function isStepReached(steps, label) {
  const step = steps.find(s => s.label === label);
  return step?.stato === 'corrente' || step?.stato === 'completato';
}

/* ═══════════════════════════════
   HEADER PAZIENTE (Anagrafica + AI Fragilità + CTA)
   ═══════════════════════════════ */
function PatientHeader({ patient }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const initials = `${patient.cognome[0]}${patient.nome[0]}`;
  const pct = patient.fragilitaAI.punteggio;
  const circumference = 2 * Math.PI * 54;
  const strokeDash = (pct / 100) * circumference;

  return (
    <>
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden relative" data-testid="patient-header-card">
      {/* Menu tre puntini — alto a destra */}
      <div className="absolute top-6 right-6 z-10" data-testid="header-menu-container">
        <button
          onClick={() => setIsMenuOpen(prev => !prev)}
          className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="header-menu-trigger"
        >
          <MoreVertical className="w-5 h-5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" />
        </button>
        {isMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
            <div className="absolute top-10 right-0 w-52 bg-white dark:bg-slate-800 shadow-lg rounded-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden" data-testid="header-dropdown-menu">
              <div
                onClick={() => { setIsRecordOpen(true); setIsMenuOpen(false); }}
                className="flex items-center gap-2.5 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer text-sm text-slate-700 dark:text-slate-200 transition-colors"
                data-testid="menu-open-scheda"
              >
                <Eye size={16} className="text-slate-500 dark:text-slate-400" /> Apri Scheda Paziente
              </div>
              <div className="h-px bg-slate-100 dark:bg-slate-700" />
              <div
                className="flex items-center gap-2.5 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer text-sm text-green-600 dark:text-green-500 transition-colors"
                data-testid="menu-chiama"
              >
                <Phone size={16} /> Chiama Paziente
              </div>
            </div>
          </>
        )}
      </div>

      <CardContent className="p-0">
        <div className="flex flex-col lg:flex-row">
          <div className="flex-1 p-7 lg:p-8" data-testid="patient-demographics">
            <div className="flex items-start gap-5">
              <div className="w-[72px] h-[72px] rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0 shadow-sm">
                <span className="text-xl font-bold text-slate-500 dark:text-slate-300">{initials}</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-foreground" data-testid="patient-full-name">
                  {patient.cognome} {patient.nome}
                </h2>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 border-0 text-[10px] font-semibold">{patient.sesso} · {patient.eta} anni</Badge>
                  <Badge variant="outline" className="text-[10px]">{patient.cia}</Badge>
                  <Badge className="border-0 text-[10px] font-semibold" style={{ backgroundColor: `${patient.fragilitaAI.colore}15`, color: patient.fragilitaAI.colore }}>
                    {patient.tipoPAI}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5 mt-6">
              {[
                { icon: Calendar, label: 'Data di nascita', value: patient.dataNascita },
                { icon: ShieldAlert, label: 'Codice Fiscale', value: patient.codiceFiscale },
                { icon: MapPin, label: 'Residenza', value: patient.residenza },
                { icon: Phone, label: 'Telefono', value: patient.telefono },
                { icon: Stethoscope, label: 'Medico curante', value: patient.medico },
                { icon: Building2, label: 'Provider', value: patient.provider },
              ].map((row) => {
                const Icon = row.icon;
                return (
                  <div key={row.label} className="flex items-start gap-2.5">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">{row.label}</p>
                      <p className="text-xs font-medium text-foreground mt-0.5 truncate">{row.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 pt-5 border-t border-border/40">
              <div className="flex items-start gap-2.5 mb-3">
                <Heart className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Diagnosi Principale</p>
                  <p className="text-xs font-semibold text-foreground mt-0.5" data-testid="patient-diagnosis">{patient.diagnosi}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 ml-6">
                {patient.comorbidita.map((c) => (
                  <Badge key={c} variant="outline" className="text-[9px] font-medium">{c}</Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:w-[280px] border-t lg:border-t-0 lg:border-l border-border/40 bg-muted/10 flex flex-col items-center justify-center p-7 lg:p-8 pt-14 lg:pt-14" data-testid="patient-ai-index">
            <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-4">Indice di Fragilita</p>
            <div className="relative w-[130px] h-[130px]">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="54" fill="none" stroke="currentColor" strokeWidth="8" className="text-border/30" />
                <motion.circle
                  cx="60" cy="60" r="54" fill="none"
                  stroke={patient.fragilitaAI.colore}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: circumference - strokeDash }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold" style={{ color: patient.fragilitaAI.colore }} data-testid="ai-score">{pct}%</span>
              </div>
            </div>
            <Badge className="mt-3 border-0 text-[11px] font-bold px-3 py-1" style={{ backgroundColor: `${patient.fragilitaAI.colore}18`, color: patient.fragilitaAI.colore }} data-testid="ai-risk-badge">
              Rischio {patient.fragilitaAI.livello}
            </Badge>
            <div className="flex items-center gap-1.5 mt-3">
              <Brain className="w-3 h-3 text-muted-foreground" />
              <p className="text-[9px] text-muted-foreground text-center leading-tight">Calcolato dal modello AI<br />sulle comorbidita</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    <PatientRecordModal open={isRecordOpen} onClose={() => setIsRecordOpen(false)} patient={patient} />
    </>
  );
}

/* ═══════════════════════════════
   TIMELINE — PERCORSO ADI (Stepper orizzontale)
   ═══════════════════════════════ */
const STEPS_ADI = [
  'Presa in Carico',
  'Valutazione UVM',
  'Definizione PAI',
  'Assegnazione Operatore',
  'Attivazione PAI',
  'Rivalutazione PAI',
  'Scadenza PAI',
];

function PAITimeline({ steps: patientSteps, overrideStepIndex }) {
  const stateConfig = {
    completato: { color: '#10B981', bg: 'bg-emerald-50', ring: 'ring-emerald-200', icon: CheckCircle2 },
    corrente: { color: '#8B5CF6', bg: 'bg-violet-50', ring: 'ring-violet-300', icon: Activity },
    futuro: { color: '#94A3B8', bg: 'bg-slate-50', ring: 'ring-slate-200', icon: Circle },
  };

  /* Determina l'indice dello step corrente — usa override se fornito */
  const currentStepIndex = overrideStepIndex != null ? overrideStepIndex : (() => {
    const idx = patientSteps.findIndex(s => s.stato === 'corrente');
    if (idx >= 0) {
      const mapped = STEPS_ADI.indexOf(patientSteps[idx].label);
      return mapped >= 0 ? mapped : idx;
    }
    return patientSteps.filter(s => s.stato === 'completato').length;
  })();

  /* Costruisce SEMPRE 7 step con stato derivato dall'indice */
  const fullSteps = STEPS_ADI.map((label, i) => {
    const match = patientSteps.find(s => s.label === label);
    const stato = i < currentStepIndex ? 'completato' : i === currentStepIndex ? 'corrente' : 'futuro';
    return { label, stato, data: match?.data || null, operatore: match?.operatore || null };
  });

  const n = fullSteps.length; // sempre 7

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="pai-timeline-card">
      <CardContent className="p-7 lg:p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
            <Activity className="w-4 h-4 text-violet-500" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground" data-testid="timeline-title">Percorso ADI</h2>
            <p className="text-[11px] text-muted-foreground">Tracciamento logistico degli step obbligatori</p>
          </div>
        </div>

        {/* Stepper — sempre 7 nodi */}
        <div className="relative pt-6" data-testid="timeline-stepper">
          {/* Full-width base line */}
          <div className="hidden sm:block absolute top-[46px] left-0 right-0 h-1 bg-slate-300 dark:bg-slate-600 rounded-full z-0" />

          {/* Colored progress line */}
          {(() => {
            const startPct = 100 / (2 * n);
            const endPct = ((2 * currentStepIndex + 1) / (2 * n)) * 100;
            return <div className="hidden sm:block absolute top-[46px] h-[3px] rounded-full bg-emerald-400 z-[1] transition-all duration-700" style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }} data-testid="timeline-progress-bar" />;
          })()}

          {/* Day labels — solo tra step con entrambe le date */}
          <div className="hidden sm:block">
            {fullSteps.map((step, i) => {
              if (i === 0) return null;
              const prev = fullSteps[i - 1];
              const days = prev.data && step.data ? daysBetween(prev.data, step.data) : null;
              if (days === null) return null;
              return (
                <div
                  key={`days-${i}`}
                  className="absolute flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-border/40 shadow-sm z-20"
                  style={{ left: `${(i / n) * 100}%`, transform: 'translateX(-50%)', top: '36px' }}
                  data-testid={`days-label-${i}`}
                >
                  <Clock className="w-2.5 h-2.5 text-muted-foreground" />
                  <span className="text-[9px] font-semibold text-muted-foreground whitespace-nowrap">+{days}gg</span>
                </div>
              );
            })}
          </div>

          {/* Step circles — SEMPRE 7 */}
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-0 relative">
            {fullSteps.map((step, i) => {
              const cfg = stateConfig[step.stato];
              const Icon = cfg.icon;
              const prev = i > 0 ? fullSteps[i - 1] : null;
              const days = prev && prev.data && step.data ? daysBetween(prev.data, step.data) : null;

              return (
                <div key={i} className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-0 flex-1" data-testid={`timeline-step-${i}`}>
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.12, duration: 0.3 }}
                    className={`relative z-10 w-11 h-11 rounded-full ${cfg.bg} ring-2 ${cfg.ring} flex items-center justify-center flex-shrink-0 ${step.stato === 'corrente' ? 'shadow-md' : ''}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: cfg.color }} />
                    {step.stato === 'corrente' && (
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-violet-500 ring-2 ring-white animate-pulse" />
                    )}
                  </motion.div>

                  <div className="sm:mt-3 sm:text-center">
                    <p className={`text-xs font-semibold ${step.stato === 'futuro' ? 'text-muted-foreground' : 'text-foreground'}`}>{step.label}</p>
                    {step.data && <p className="text-[10px] text-muted-foreground mt-0.5">{step.data}</p>}
                    {step.operatore && (
                      <p className={`text-[10px] mt-0.5 ${step.stato === 'corrente' ? 'text-violet-500 font-semibold' : 'text-muted-foreground'}`}>{step.operatore}</p>
                    )}
                    {days !== null && (
                      <div className="sm:hidden flex items-center gap-1 mt-1">
                        <Clock className="w-2.5 h-2.5 text-muted-foreground" />
                        <span className="text-[9px] font-semibold text-muted-foreground">+{days} giorni</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mt-8 pt-4 border-t border-border/40">
          {[
            { color: '#10B981', label: 'Completato' },
            { color: '#8B5CF6', label: 'Step Corrente' },
            { color: '#94A3B8', label: 'Da completare' },
          ].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
              <span className="text-[10px] text-muted-foreground font-medium">{l.label}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════
   PAGE PRINCIPALE
   ═══════════════════════════════ */
export default function CaseManagerPatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patient = patientDetails[id];
  const [paiAdvanced, setPaiAdvanced] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reevalOpen, setReevalOpen] = useState(!!location.state?.openRivalutazione);
  const [paiCompileOpen, setPaiCompileOpen] = useState(false);
  const [paiStatus, setPaiStatus] = useState(patient?.paiStatus || null);
  const [plannedData, setPlannedData] = useState(location.state?.isPlanned ? location.state : null);

  if (!patient) {
    return (
      <AppLayout role="case-manager">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-sm text-muted-foreground">Paziente non trovato.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/case-manager')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Torna al Cruscotto
          </Button>
        </div>
      </AppLayout>
    );
  }

  /* Indice step corrente basato su STEPS_ADI (costante a 7 step) */
  const baseIdx = (() => {
    const idx = patient.paiSteps.findIndex(s => s.stato === 'corrente');
    if (idx >= 0) {
      const mapped = STEPS_ADI.indexOf(patient.paiSteps[idx].label);
      return mapped >= 0 ? mapped : idx;
    }
    return patient.paiSteps.filter(s => s.stato === 'completato').length;
  })();
  const currentIdx = paiAdvanced ? baseIdx + 1 : baseIdx;

  const showPAIBuilder = currentIdx === STEPS_ADI.indexOf('Definizione PAI');
  const showGantt = currentIdx >= STEPS_ADI.indexOf('Attivazione PAI') && ganttData[id];
  const showAssegnazione = currentIdx === STEPS_ADI.indexOf('Assegnazione Operatore') && paiStatus !== 'DA_COMPLETARE_CM';
  const showRivalutazione = currentIdx === STEPS_ADI.indexOf('Rivalutazione PAI');
  const showCompletaPAI = showPAIBuilder && paiStatus === 'DA_COMPLETARE_CM';

  const handleAssegnazioneConfirm = () => {
    setPaiAdvanced(true);
  };

  const handlePaiCompleted = () => {
    setPaiStatus('COMPLETO');
    setPaiCompileOpen(false);
    setPaiAdvanced(true);
  };

  return (
    <AppLayout role="case-manager">
      <div className="flex flex-col gap-8" data-testid="cm-patient-detail">
        {/* Back button */}
        <button
          onClick={() => navigate('/case-manager')}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Torna al Cruscotto
        </button>

        {/* Header */}
        <PatientHeader patient={patient} />

        {/* Timeline — Percorso ADI */}
        <PAITimeline steps={patient.paiSteps} overrideStepIndex={paiAdvanced ? currentIdx : undefined} />

        {/* CTA: Assegnazione Provider — SOLO quando step corrente = Assegnazione Operatore */}
        {showAssegnazione && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-violet-500" data-testid="assegnazione-cta">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-sm">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Assegnazione Erogatore</p>
                  <p className="text-[11px] text-muted-foreground">Seleziona un Provider esterno o assegna risorse interne ASL</p>
                </div>
              </div>
              <Button
                className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
                onClick={() => setDrawerOpen(true)}
                data-testid="open-assegnazione-btn"
              >
                <ArrowRight className="w-4 h-4" />Procedi con Assegnazione Provider
              </Button>
            </CardContent>
          </Card>
        )}

        {/* CTA: Completa Pianificazione PAI — SOLO se paiStatus = DA_COMPLETARE_CM */}
        {showCompletaPAI && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-amber-500" data-testid="completa-pai-cta">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                  <ClipboardEdit className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Completa Pianificazione PAI</p>
                  <p className="text-[11px] text-muted-foreground">Il medico UVMD ha pre-compilato i dati clinici. Definisci gli interventi e la telemedicina.</p>
                </div>
              </div>
              <Button
                className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-sm"
                onClick={() => setPaiCompileOpen(true)}
                data-testid="open-completa-pai-btn"
              >
                <ClipboardEdit className="w-4 h-4" />Completa Pianificazione PAI
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Gantt Clinico-Logistico — SOLO da Attivazione PAI in poi */}
        {showGantt && <ClinicalGantt data={ganttData[id]} paiSteps={patient.paiSteps} />}

        {/* CTA: Rivalutazione PAI — SOLO quando step corrente = Rivalutazione PAI */}
        {showRivalutazione && !plannedData && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-amber-500" data-testid="rivalutazione-cta">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                  <RefreshCw className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Pianificazione Rivalutazione</p>
                  <p className="text-[11px] text-muted-foreground">Convoca l'équipe UVMD per la rivalutazione del paziente</p>
                </div>
              </div>
              <Button
                className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-sm"
                onClick={() => setReevalOpen(true)}
                data-testid="open-rivalutazione-btn"
              >
                <RefreshCw className="w-4 h-4" />Pianifica Rivalutazione
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Box Riepilogo — Rivalutazione Programmata (Card verticali stile Équipe Individuata) */}
        {plannedData && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl border-l-4 border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/10" data-testid="rivalutazione-planned">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                    <CalendarCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Rivalutazione UVMD Programmata</p>
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400">In attesa dell'esito clinico</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-lg text-xs gap-1.5 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30" onClick={() => { setReevalOpen(true); }} data-testid="modifica-appuntamento-btn">
                  <RefreshCw className="w-3 h-3" /> Modifica Appuntamento
                </Button>
              </div>

              <div className="flex flex-col gap-2">
                {/* Data/Ora card */}
                <div className="bg-white dark:bg-slate-900 rounded-lg border border-emerald-200 dark:border-emerald-800/40 px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-800 dark:text-white">Visita UVMD programmata per:</p>
                    <p className="text-xs text-emerald-700 dark:text-emerald-300">{plannedData.date || 'Mer 22 Aprile'} alle ore {plannedData.time || '10:00'}</p>
                  </div>
                </div>

                {/* Professionisti cards */}
                {(plannedData.team || []).map((member, i) => {
                  const label = typeof member === 'string' ? member : member.label;
                  const nome = typeof member === 'string' ? null : member.nome;
                  return (
                    <div key={i} className="bg-white dark:bg-slate-900 rounded-lg border border-emerald-200 dark:border-emerald-800/40 px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-800 dark:text-white">{nome || label}</p>
                        {nome && <p className="text-[10px] text-slate-500 dark:text-slate-400">{label}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA: PAI Builder — SOLO quando step corrente = Definizione PAI */}
        {showPAIBuilder && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl" data-testid="pai-builder-cta">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shadow-sm">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">PAI Builder con AI</p>
                  <p className="text-[11px] text-muted-foreground">Costruisci il Piano Assistenziale con supporto decisionale AI</p>
                </div>
              </div>
              <Button
                className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm"
                onClick={() => navigate(`/case-manager/paziente/${id}/pai-builder`)}
                data-testid="open-pai-builder-btn"
              >
                <Sparkles className="w-4 h-4" />Apri PAI Builder
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Drawer Assegnazione */}
      <AssegnazioneProviderDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        patient={patient}
        onConfirm={handleAssegnazioneConfirm}
      />

      {/* Full-screen Rivalutazione PAI */}
      <PaiReevaluationScreen
        open={reevalOpen}
        onClose={(payload) => {
          setReevalOpen(false);
          if (payload?.isPlanned) setPlannedData(payload);
        }}
        patient={patient}
        editData={plannedData}
      />

      {/* Full-screen Compilazione PAI (Case Manager) */}
      {paiCompileOpen && (
        <div className="fixed inset-0 z-50 bg-background" data-testid="pai-compile-fullscreen">
          <PaiManualCompilation
            patient={patient}
            onBack={() => setPaiCompileOpen(false)}
            completeEvaluation={() => handlePaiCompleted()}
            cmMode
            uvmdNotes={patient.uvmdNotes}
          />
        </div>
      )}
    </AppLayout>
  );
}
